import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Building2, Search, Pencil, Trash2, Loader2 } from "lucide-react";

const EMPTY = { indirizzo: "", citta: "", reparto_citofono: "", piano: "", scala: "", ascensore: "" };

export default function EntitiesPage() {
  const { user } = useAuth();
  const canEdit = user?.staff_role !== "operatore";
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (q = "") => {
    setItems(null);
    try {
      const { data } = await api.get("/locations", { params: q ? { q } : {} });
      setItems(data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, load]);

  const openEdit = (loc) => {
    setForm({
      indirizzo: loc.indirizzo || "", citta: loc.citta || "",
      reparto_citofono: loc.reparto_citofono || "", piano: loc.piano || "",
      scala: loc.scala || "", ascensore: loc.ascensore || "",
    });
    setEditing(loc);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/locations/${editing.id}`, form);
      toast.success("Ente / indirizzo aggiornato", { description: "Le richieste collegate sono state allineate." });
      setEditing(null);
      await load(query.trim());
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Eliminare "${loc.citta || ""} — ${loc.indirizzo || ""}" dall'anagrafica?\n\nNon verranno eliminate le richieste collegate.`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success("Ente / indirizzo eliminato");
      await load(query.trim());
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <div className="mb-6">
          <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
            <Building2 className="h-7 w-7 text-primary" /> Enti / Indirizzi
          </h1>
          <p className="text-sm text-slate-500 mt-1">Enti e indirizzi di partenza/arrivo salvati automaticamente. Cerca, modifica ed elimina le voci.</p>
        </div>

        <div className="relative mb-4 max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="entities-search" placeholder="Cerca per città, indirizzo o reparto..."
            value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
        </div>

        <Card className="border-slate-200 overflow-hidden">
          {items === null ? (
            <div className="p-5 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-12 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">{query ? "Nessun risultato per la ricerca." : "Nessun ente / indirizzo salvato."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Città</TableHead>
                  <TableHead>Indirizzo</TableHead>
                  <TableHead>Reparto / Citofono</TableHead>
                  <TableHead>Usi</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((loc) => (
                  <TableRow key={loc.id} data-testid={`entity-row-${loc.id}`}>
                    <TableCell className="font-medium text-slate-800 text-sm">{loc.citta || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{loc.indirizzo || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{loc.reparto_citofono || "—"}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                        {loc.usi}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit && (
                          <Button data-testid={`entity-edit-${loc.id}`} size="sm" variant="ghost" className="gap-1.5" onClick={() => openEdit(loc)}>
                            <Pencil className="h-4 w-4" /> Modifica
                          </Button>
                        )}
                        {canEdit && (
                          <Button data-testid={`entity-delete-${loc.id}`} size="icon" variant="ghost"
                            className="h-8 w-8 text-slate-400 hover:text-red-600" title="Elimina" onClick={() => remove(loc)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifica Ente / Indirizzo</DialogTitle>
              <DialogDescription>Correggi i dati. Le richieste collegate verranno aggiornate.</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveEdit} className="space-y-4 pt-2">
              <div>
                <Label className="nx-label">Città / Comune</Label>
                <Input data-testid="edit-entity-citta" className="mt-1.5"
                  value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} />
              </div>
              <div>
                <Label className="nx-label">Indirizzo Completo</Label>
                <Input data-testid="edit-entity-indirizzo" className="mt-1.5"
                  value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
              </div>
              <div>
                <Label className="nx-label">Reparto / Citofono</Label>
                <Input data-testid="edit-entity-reparto" className="mt-1.5"
                  value={form.reparto_citofono} onChange={(e) => setForm({ ...form, reparto_citofono: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="nx-label">Piano</Label>
                  <Input data-testid="edit-entity-piano" className="mt-1.5"
                    value={form.piano} onChange={(e) => setForm({ ...form, piano: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Scala</Label>
                  <Input data-testid="edit-entity-scala" className="mt-1.5"
                    value={form.scala} onChange={(e) => setForm({ ...form, scala: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Ascensore</Label>
                  <Select value={form.ascensore} onValueChange={(v) => setForm({ ...form, ascensore: v })}>
                    <SelectTrigger data-testid="edit-entity-ascensore" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SI">SI</SelectItem>
                      <SelectItem value="NO">NO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="edit-entity-save" disabled={saving} className="gap-2 w-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva Modifiche"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
